import { useEffect } from 'react';
import { Link } from 'react-router';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { fetchReceipts, selectReceipts, selectReceiptsStatus } from '@features/receipts';
import type { Receipt } from '@features/receipts';
import { formatAmount } from '@shared/lib/format';
import { paths } from '@shared/config/paths';
import { Button } from '@shared/ui/button/Button';
import { Card, CardContent, CardHeader } from '@shared/ui/card/Card';
import { Icon } from '@shared/ui/icon/Icon';
import type { IconName } from '@shared/ui/icon/Icon';
import { Skeleton } from '@shared/ui/skeleton/Skeleton';
import { AiProcessingCard } from '../components/AiProcessingCard';
import { CategoryDonut } from '../components/CategoryDonut';
import { ExpensesAreaChart } from '../components/ExpensesAreaChart';
import { RecentActivityFeed } from '../components/RecentActivityFeed';

/* ---------- KPI ---------- */

interface Kpi {
  label: string;
  value: string;
  /** Tendance (ex. "+12.4% vs mois précédent") ou rien. */
  trend?: { value: string; positive: boolean };
  sub: string;
  icon: IconName;
}

function buildKpis(receipts: Receipt[]): Kpi[] {
  const totalAmount = receipts.reduce((sum, r) => sum + r.total, 0);
  const currency = receipts[0]?.currency ?? 'EUR';

  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevMonthRef = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthKey = `${prevMonthRef.getFullYear()}-${String(prevMonthRef.getMonth() + 1).padStart(2, '0')}`;

  const thisMonthTotal = receipts
    .filter((r) => r.date.startsWith(monthKey))
    .reduce((sum, r) => sum + r.total, 0);
  const prevMonthTotal = receipts
    .filter((r) => r.date.startsWith(prevMonthKey))
    .reduce((sum, r) => sum + r.total, 0);
  const thisMonthCount = receipts.filter((r) => r.date.startsWith(monthKey)).length;

  const avgReceipt = receipts.length === 0 ? 0 : totalAmount / receipts.length;

  const trendPercent =
    prevMonthTotal > 0
      ? Math.round(((thisMonthTotal - prevMonthTotal) / prevMonthTotal) * 1000) / 10
      : null;

  return [
    {
      label: 'Dépenses totales',
      value: receipts.length === 0 ? '—' : formatAmount(totalAmount, currency),
      trend:
        trendPercent !== null
          ? {
              value: `${trendPercent >= 0 ? '+' : ''}${trendPercent} % vs mois précédent`,
              positive: trendPercent <= 0,
            }
          : undefined,
      sub: 'Toutes dépenses confondues',
      icon: 'wallet',
    },
    {
      label: 'Tickets traités',
      value: String(receipts.length),
      trend:
        thisMonthCount > 0 ? { value: `+${thisMonthCount} ce mois-ci`, positive: true } : undefined,
      sub: 'Documents traités',
      icon: 'receipt',
    },
    {
      label: 'Ticket moyen',
      value: receipts.length === 0 ? '—' : formatAmount(avgReceipt, currency),
      sub: 'Montant moyen par ticket',
      icon: 'chart',
    },
  ];
}

/* ---------- Page ---------- */

export function DashboardPage() {
  const dispatch = useAppDispatch();
  const receipts = useAppSelector(selectReceipts);
  const receiptsStatus = useAppSelector(selectReceiptsStatus);

  // Charge les receipts depuis l'API si ce n'est pas déjà fait.
  useEffect(() => {
    if (receiptsStatus === 'idle') {
      void dispatch(fetchReceipts());
    }
  }, [dispatch, receiptsStatus]);

  const loading = receiptsStatus === 'loading' || receiptsStatus === 'idle';
  const kpis = buildKpis(receipts);

  return (
    <section className="flex flex-col gap-6">
      {/* En-tête de page + CTA */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">Vue d'ensemble</h2>
          <p className="text-sm text-slate-500">
            Tickets scannés → Extraction IA → Analyse financière
          </p>
        </div>
        <Link to={paths.receiptNew} tabIndex={-1}>
          <Button>
            <Icon name="upload" size={16} />
            Ajouter un ticket
          </Button>
        </Link>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <Card key={i}>
                <CardContent className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))
          : kpis.map((kpi) => (
              <Card key={kpi.label}>
                <CardContent className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-medium text-slate-500">{kpi.label}</span>
                    <span className="flex size-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                      <Icon name={kpi.icon} size={16} />
                    </span>
                  </div>
                  <span className="text-[26px] leading-8 font-semibold tracking-tight text-slate-900">
                    {kpi.value}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {kpi.trend ? (
                      <span
                        className={
                          kpi.trend.positive
                            ? 'inline-flex items-center gap-1 text-xs font-medium text-emerald-600'
                            : 'inline-flex items-center gap-1 text-xs font-medium text-slate-500'
                        }
                      >
                        <Icon name="trending-up" size={13} />
                        {kpi.trend.value}
                      </span>
                    ) : null}
                    <span className="text-xs text-slate-400">{kpi.sub}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
      </div>

      {/* Dépenses mensuelles + Répartition par catégorie */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Aperçu des dépenses"
            subtitle="Dépenses mensuelles sur les 5 derniers mois"
          />
          <CardContent>
            <ExpensesAreaChart receipts={receipts} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Dépenses par catégorie" subtitle="Répartition des dépenses" />
          <CardContent>
            <CategoryDonut receipts={receipts} />
          </CardContent>
        </Card>
      </div>

      {/* Aperçu du budget + Traitement IA */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Activité récente" subtitle="Derniers tickets traités" />
          <CardContent>
            <RecentActivityFeed receipts={receipts} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Traitement IA" subtitle="Santé de l'extraction IA" />
          <CardContent>
            <AiProcessingCard receipts={receipts} />
          </CardContent>
        </Card>
      </div>
      {/* Top marchands + Activité récente */}
      {/*       <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Top commerçants" subtitle="Où partent vos dépenses" />
          <CardContent>
            <TopMerchantsChart receipts={receipts} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Aperçu du budget"
            subtitle="Budget consommé ce mois-ci par catégorie"
          />

          <CardContent>
            <BudgetOverview receipts={receipts} />
          </CardContent>
        </Card>
      </div> */}
    </section>
  );
}
